import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type InputHTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ChangeEvent,
  type ReactNode,
  type Ref,
} from 'react'
import { cx, isFilled, useControllableState, useDebouncedValue } from '@/shared/lib'
import { Divider } from '@/shared/ui/Divider'
import { Icon } from '@/shared/ui/Icon'
import { IconButton } from '@/shared/ui/IconButton'
import { Input } from '@/shared/ui/Input'
import { Kbd } from '@/shared/ui/Kbd'
import { Popover } from '@/shared/ui/Popover'
import { SectionCaption } from '@/shared/ui/SectionCaption'
import { Skeleton } from '@/shared/ui/Skeleton'
import { Spinner } from '@/shared/ui/Spinner'
import s from './SearchAutocomplete.module.css'

/** Ступени поля: `lg` 44 — витрина каталога и мобильная шапка, `md` 38 — десктопная шапка. */
export type SearchAutocompleteSize = 'md' | 'lg'

/** Тон хвоста второй строки: остаток на складе, срок поставки, снятая позиция. */
export type SearchAutocompleteItemTone = 'default' | 'success' | 'warning' | 'danger'

/** Служебные пропсы строки для слота `render`: раскладываются на свой элемент как есть. */
export type SearchAutocompleteItemSlotProps = {
  /** Обязателен: по нему `aria-activedescendant` находит строку, а клавиатура её нажимает. */
  id: string
  className: string
  children: ReactNode
  role: 'option'
  tabIndex: -1
  'aria-selected': boolean
  'aria-disabled': true | undefined
  'data-search-item': string
  onClick: () => void
}

export type SearchAutocompleteItem = {
  /** Уникален в пределах всей выдачи: он же ключ строки и идентификатор подсветки. */
  id: string
  /** Подпись. `ReactNode`, чтобы принять подсветку совпадений, собранную потребителем. */
  label: ReactNode
  /** Артикул моноширинным под подписью. */
  sku?: ReactNode
  /** Хвост второй строки: остаток, срок поставки, «нет прав». */
  meta?: ReactNode
  metaTone?: SearchAutocompleteItemTone
  /** Слот слева: миниатюра товара, плашка бренда, чип артикула, часы у недавнего запроса. */
  media?: ReactNode
  /** Хвост строки справа: цена, число товаров бренда. Не сжимается. */
  trailing?: ReactNode
  /** Пункт-переход: `<a href>` со средним кликом и «открыть в новой вкладке». */
  href?: string
  /** Пункт-переход роутера: элемент создаёт потребитель — примитив о роутере не знает. */
  render?: (props: SearchAutocompleteItemSlotProps) => ReactNode
  /** Позиция существует, но недоступна. Остаётся видимой и объявляется выключенной. */
  disabled?: boolean
}

export type SearchAutocompleteGroup = {
  id: string
  /** Подпись группы **в обычном регистре**: заглавные ставит CSS. Даёт группе имя. */
  label: string
  items: SearchAutocompleteItem[]
}

export type SearchAutocompleteProps = {
  /** Выдача, разложенная бэкендом. Внутри не группируется, не сортируется, не фильтруется. */
  groups?: SearchAutocompleteGroup[]
  /** Управляемый режим: текст запроса живёт у потребителя (синхронизация с `?q=`). */
  value?: string
  /** Неуправляемый режим: стартовый текст, дальше его держит компонент. */
  defaultValue?: string
  /** Текст поля изменился. **Без задержки** — это значение, а не запрос. */
  onChange?: (query: string) => void
  /** Запрос **уже отложен** на `SEARCH_DEBOUNCE_MS`. Уходит в аргумент RTK Query. */
  onSearch?: (query: string) => void
  /** Выбрана подсказка. У пунктов с `href`/`render` переход делает сама ссылка. */
  onSelect?: (item: SearchAutocompleteItem, group: SearchAutocompleteGroup) => void
  /** «Показать все»: Enter без подсвеченной строки либо нажатие по футеру. */
  onSubmit?: (query: string) => void
  /** Управляемое раскрытие: ⌘K открывает поиск, стенд показывает ветки панели. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Идёт запрос: строка со спиннером и три заглушки, `aria-busy` на списке. */
  loading?: boolean
  /** Подпись строки загрузки: «Ищем в 1С…». Пусто — остаются только заглушки. */
  loadingLabel?: ReactNode
  /** Строк нет: «Ничего не найдено…» либо «Введите артикул, бренд или название». */
  empty?: ReactNode
  /** Запрос не удался: текст плюс «Повторить» от потребителя. */
  error?: ReactNode
  /** Футер «Показать все 1 284 результата». Пусто — футера нет, Enter всё равно отправляет. */
  submitLabel?: ReactNode
  /** Доступное имя поля и списка подсказок. */
  label: string
  placeholder?: string
  /** Доступное имя кнопки очистки. Не задан — кнопки нет: безымянных кнопок не бывает. */
  clearLabel?: string
  /** Слот в хвосте поля: `<Kbd keys={['mod','K']} />`. Компонент про ускоритель не знает. */
  suffix?: ReactNode
  size?: SearchAutocompleteSize
  /** Поиск недоступен: нет прав, 1С не отвечает. Панель не раскрывается. */
  disabled?: boolean
  /** Указывает на сам `input`: программный фокус по ⌘K, `register()` из react-hook-form. */
  ref?: Ref<HTMLInputElement>
} & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  | 'value'
  | 'defaultValue'
  | 'onChange'
  | 'onSelect'
  | 'onSubmit'
  | 'size'
  | 'prefix'
  | 'children'
  | 'type'
  | 'role'
>

/** Строка выдачи в плоском порядке клавиатуры: сама позиция, её группа и место в обходе. */
type Row = {
  item: SearchAutocompleteItem
  group: SearchAutocompleteGroup
  index: number
}

/** Ссылка стабильна между рендерами: умолчание-литерал гоняло бы эффекты впустую. */
const EMPTY_GROUPS: SearchAutocompleteGroup[] = []

/** Ширины строк-заглушек: одинаковые читались бы полосатым блоком, а не выдачей. */
const SKELETON_WIDTHS = ['long', 'full', 'medium'] as const

// CSS-модуль типизирован индексной сигнатурой, поэтому значения — string | undefined
const TONE_CLASS: Record<SearchAutocompleteItemTone, string | undefined> = {
  default: undefined,
  success: s.metaSuccess,
  warning: s.metaWarning,
  danger: s.metaDanger,
}

/**
 * Глобальный поиск по системе: артикул, бренд, название. Бэкенд отвечает верхушкой выдачи,
 * разложенной по группам, пользователь либо проваливается в объект, либо уходит на страницу
 * результатов целиком.
 *
 * Это точка входа, а не поле формы: значения у компонента нет, наружу уходит **намерение** —
 * `onSelect(item, group)` (переход к объекту) или `onSubmit(query)` (показать всю выдачу).
 * Поэтому первая строка не подсвечивается автоматически: Enter обязан отправлять запрос,
 * ровно как обещает футер «показать все ↵». Это и есть граница с `Combobox`, у которого выбор —
 * значение и первая строка подсвечена всегда.
 *
 * Поиск серверный (правило 13): текст откладывается на `SEARCH_DEBOUNCE_MS` и уходит в `onSearch`.
 * Внутри нет ни одного `filter`, `sort` или `groupBy` по выдаче — что показать, решает бэкенд
 * (правило 11). Пагинации у подсказок нет и не будет: они верхушка, а полный список живёт
 * на странице результатов, где работает `Pagination` (правило 14 — разбор в контракте).
 *
 * Контракт: docs/ui-kit/contracts/search-autocomplete.md
 */
export function SearchAutocomplete({
  groups = EMPTY_GROUPS,
  value,
  defaultValue,
  onChange,
  onSearch,
  onSelect,
  onSubmit,
  open,
  onOpenChange,
  loading = false,
  loadingLabel,
  empty,
  error,
  submitLabel,
  label,
  placeholder,
  clearLabel,
  suffix,
  size = 'lg',
  disabled = false,
  className,
  onFocus,
  onClick,
  onKeyDown,
  ref,
  ...rest
}: SearchAutocompleteProps) {
  const reactId = useId()
  const panelId = `${reactId}-panel`
  const listboxId = `${reactId}-listbox`
  const rowDomId = (index: number) => `${reactId}-r${index}`

  const rootRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  /*
   * Последний отданный наружу запрос. Инициализируется начальным текстом: страница результатов
   * монтирует поле уже заполненным из адреса, и лишний запрос подсказок ей не нужен.
   */
  const searched = useRef(value ?? defaultValue ?? '')

  const [query, setQuery] = useControllableState<string>(value, defaultValue ?? '', onChange)
  const [isOpen, setOpen] = useControllableState<boolean>(open, false, onOpenChange)
  const [activeId, setActiveId] = useState<string | null>(null)

  const debouncedQuery = useDebouncedValue(query)

  /*
   * Плоский порядок обхода и разметка групп собираются одним проходом. Это не преобразование
   * серверных данных (правило 11): состав и порядок остаются ровно теми, что пришли, —
   * из массива ничего не выпадает и ничто не меняется местами.
   */
  const rows: Row[] = []
  const sections = groups.map((group, groupIndex) => ({
    group,
    captionId: `${reactId}-g${groupIndex}`,
    rows: group.items.map((item) => {
      const row: Row = { item, group, index: rows.length }
      rows.push(row)
      return row
    }),
  }))

  // Выключенные строки рисуются, но стрелками пропускаются: клавиатура не должна упираться в тупик
  const navigable = rows.filter((row) => row.item.disabled !== true)

  /*
   * Активная строка выводится, а не хранится «как есть»: выдача меняется под руками пользователя,
   * и строка из прошлого ответа не должна ни залипать подсветкой, ни уехать в onSelect.
   * Не нашлась — активной нет, и Enter снова означает «показать все».
   */
  const active = navigable.some((row) => row.item.id === activeId) ? activeId : null
  const activeRow = rows.find((row) => row.item.id === active)

  const hasRows = rows.length > 0
  const showEmpty = !loading && !hasRows && !isFilled(error) && isFilled(empty)
  const showError = isFilled(error)
  const showFooter = isFilled(submitLabel)
  // Пустой панели не бывает: раскрывать пустое стекло поверх контента незачем
  const hasPanel = hasRows || loading || showEmpty || showError || showFooter
  const expanded = isOpen && !disabled && hasPanel

  const activeDescendant =
    expanded && activeRow !== undefined ? rowDomId(activeRow.index) : undefined

  /** Поле нужно и снаружи (⌘K, программный фокус), и внутри: ref раздваивается. */
  const attachInput = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node
      // ref-колбэк потребителя в React 19 вправе вернуть cleanup — его нельзя терять
      if (typeof ref === 'function') return ref(node)
      if (ref) ref.current = node
      return undefined
    },
    [ref],
  )

  /*
   * Единственный выход запроса наружу — уже отложенного. Сырого «на каждое нажатие» колбэка
   * под этим именем нет намеренно: иначе первый же потребитель повесил бы на него запрос
   * без задержки (правило 13). Сырой текст доступен через onChange — это значение поля.
   */
  useEffect(() => {
    if (searched.current === debouncedQuery) return

    searched.current = debouncedQuery
    onSearch?.(debouncedQuery)
  }, [debouncedQuery, onSearch])

  // Подсвеченная строка может уехать за нижний край панели: стрелка обязана вернуть её в кадр
  useEffect(() => {
    if (activeDescendant === undefined) return

    // scrollIntoView в jsdom не реализован — отсутствие API не должно ронять тесты
    document.getElementById(activeDescendant)?.scrollIntoView?.({ block: 'nearest' })
  }, [activeDescendant])

  const openPanel = () => {
    if (disabled || isOpen) return
    setOpen(true)
  }

  /** Запрос закрытие не трогает: текст — значение поля и переживает панель (в отличие от Combobox). */
  const closePanel = () => {
    setActiveId(null)
    if (isOpen) setOpen(false)
  }

  const keepFocus = () => inputRef.current?.focus({ preventScroll: true })

  const selectRow = (row: Row) => {
    if (row.item.disabled === true) return

    onSelect?.(row.item, row.group)
    /*
     * Фокус возвращать некому и незачем: мышью он не уходил (mousedown по списку погашен),
     * а с клавиатуры он всё это время оставался в поле — по строкам ходит aria-activedescendant.
     */
    closePanel()
  }

  const submit = () => {
    onSubmit?.(query)
    closePanel()
    keepFocus()
  }

  /**
   * Клавиатура нажимает **ту же самую строку**, что и мышь: у пункта-ссылки переход делает
   * сама ссылка, и двух способов активации у неё быть не должно — они разъедутся на первом
   * же роутере. Узла нет (потребитель забыл разложить `id` в слоте `render`) — зовём напрямую.
   */
  const activate = (row: Row) => {
    const node = document.getElementById(rowDomId(row.index))
    if (node === null) selectRow(row)
    else node.click()
  }

  /** Список не зациклен, но верхний край особый: с первой строки подсветка снимается (APG). */
  const move = (delta: 1 | -1) => {
    if (navigable.length === 0) return

    const current = navigable.findIndex((row) => row.item.id === active)

    if (current < 0) {
      const edge = delta === 1 ? navigable[0] : navigable[navigable.length - 1]
      if (edge !== undefined) setActiveId(edge.item.id)
      return
    }

    if (delta === -1 && current === 0) {
      // Возврат к набранному тексту: Enter снова означает «показать все»
      setActiveId(null)
      return
    }

    const next = navigable[Math.min(current + delta, navigable.length - 1)]
    if (next !== undefined) setActiveId(next.item.id)
  }

  const handleFocus = (event: ReactFocusEvent<HTMLInputElement>) => {
    onFocus?.(event)
    openPanel()
  }

  /** Повторное нажатие по уже сфокусированному полю снова раскрывает панель: focus не придёт. */
  const handleClick = (event: ReactMouseEvent<HTMLInputElement>) => {
    onClick?.(event)
    openPanel()
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value)
    openPanel()
  }

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event)
    if (event.defaultPrevented) return
    // ⌘K и системные сочетания обязаны дойти до браузера и до приложения
    if (event.ctrlKey || event.metaKey) return

    // APG: Alt+↑ активирует подсвеченную строку и закрывает список
    if (event.altKey && event.key === 'ArrowUp') {
      if (activeRow === undefined) return

      event.preventDefault()
      activate(activeRow)
      return
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        if (expanded) move(1)
        else openPanel()
        return
      case 'ArrowUp':
        event.preventDefault()
        if (expanded) move(-1)
        else openPanel()
        return
      case 'Enter':
        /*
         * preventDefault всегда: поиск — не поле формы, и всплывшая наружу отправка сабмитила бы
         * форму вокруг. Подсветки нет — Enter означает «показать все», как обещает футер.
         */
        event.preventDefault()
        if (expanded && activeRow !== undefined) activate(activeRow)
        else submit()
        return
      case 'Escape':
        /*
         * Панель открыта — её закроет Popover, набранное не трогаем: здесь запрос это значение
         * поля, и снести его за попытку убрать оверлей значило бы наказать пользователя.
         * Панель закрыта — Esc чистит поле; preventDefault, чтобы не закрылась модалка вокруг.
         */
        if (expanded || query === '') return

        event.preventDefault()
        setQuery('')
        return
      default:
        return
    }
  }

  const handleClear = () => {
    setQuery('')
    setActiveId(null)
    keepFocus()
  }

  const clearButton =
    disabled || query === '' || clearLabel === undefined || clearLabel === '' ? null : (
      <IconButton
        icon={<Icon name="close" size="xs" />}
        label={clearLabel}
        variant="subtle"
        size="xs"
        // Нажатие не должно уводить фокус из поля: в Safari он ушёл бы в body
        onMouseDown={(event) => event.preventDefault()}
        onClick={handleClear}
      />
    )

  // Пустой слот суффикса не отдаём: Input нарисовал бы под него коробку с отступом
  const fieldSuffix =
    clearButton !== null || isFilled(suffix) ? (
      <>
        {clearButton}
        {suffix}
      </>
    ) : undefined

  const renderRow = (row: Row) => {
    const { item } = row
    const domId = rowDomId(row.index)
    const isActive = item.id === active
    const isDisabled = item.disabled === true

    const content = (
      <>
        {/* Слот не скрывается от скринридера: у группы АРТИКУЛЫ там лежит сам артикул,
            а иконка и аватар прячут себя сами */}
        {isFilled(item.media) ? <span className={s.media}>{item.media}</span> : null}

        <span className={s.text}>
          <span className={s.label}>{item.label}</span>

          {isFilled(item.sku) || isFilled(item.meta) ? (
            <span className={s.sub}>
              {isFilled(item.sku) ? <span className={s.sku}>{item.sku}</span> : null}
              {isFilled(item.meta) ? (
                <span className={cx(s.meta, TONE_CLASS[item.metaTone ?? 'default'])}>
                  {item.meta}
                </span>
              ) : null}
            </span>
          ) : null}
        </span>

        {/* Хвост остаётся в дереве доступности: цена и остаток нужны на слух */}
        {isFilled(item.trailing) ? <span className={s.trailing}>{item.trailing}</span> : null}
      </>
    )

    const base = {
      id: domId,
      className: cx(s.row, isActive && s.rowActive, isDisabled && s.rowDisabled),
      role: 'option' as const,
      tabIndex: -1 as const,
      'aria-selected': isActive,
      'aria-disabled': isDisabled ? (true as const) : undefined,
      'data-search-item': item.id,
      // Выключенную строку гасит сам selectRow: обработчик остаётся, чтобы тип слота был один
      onClick: () => selectRow(row),
    }

    // Выключенная позиция ссылкой не становится ни при каких пропсах: <a> увёл бы по href
    if (!isDisabled && item.render !== undefined) {
      return <Fragment key={item.id}>{item.render({ ...base, children: content })}</Fragment>
    }

    if (!isDisabled && item.href !== undefined) {
      return (
        <a key={item.id} {...base} href={item.href}>
          {content}
        </a>
      )
    }

    return (
      <div key={item.id} {...base}>
        {content}
      </div>
    )
  }

  const state = disabled ? 'disabled' : expanded ? 'open' : 'closed'

  /*
   * Панель — сосед обёртки, а не её потомок. Так обходился дефект якоря на первом рендере
   * (⌘K, стенд): у соседа ref якоря привязан заведомо раньше. Дефект вылечен в самом Popover
   * второй попыткой замера, и обход больше не обязателен — разметка оставлена как есть,
   * потому что панель всё равно уезжает порталом и её место в дереве React ни на что не влияет.
   */
  return (
    <>
      <div ref={rootRef} className={cx(s.root, className)} data-size={size} data-state={state}>
        <Input
          {...rest}
          ref={attachInput}
          size={size}
          variant="search"
          /* type="search" в WebKit рисует свою кнопку очистки — она встала бы второй рядом с нашей */
          type="text"
          value={query}
          placeholder={placeholder}
          disabled={disabled}
          prefix={<Icon name="search" size="sm" />}
          suffix={fieldSuffix}
          role="combobox"
          aria-expanded={expanded}
          // Ссылка ставится только при раскрытой панели: закрытой в DOM не существует
          aria-controls={expanded ? listboxId : undefined}
          aria-activedescendant={activeDescendant}
          aria-autocomplete="list"
          aria-label={label}
          /* Своя выпадашка браузера накрыла бы подсказки */
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="search"
          onChange={handleChange}
          onFocus={handleFocus}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
        />
      </div>

      <Popover
        anchorRef={rootRef}
        id={panelId}
        open={expanded}
        onOpenChange={(next) => {
          if (!next) closePanel()
        }}
        placement="bottom-start"
        /* Кит: left: 0; right: 0 — оверлей ровно по полю, в том числе на 360px */
        matchAnchorWidth
        /* Фокус остаётся в поле: по списку ходит aria-activedescendant */
        className={s.panel}
      >
        <div className={s.listArea}>
          <div
            id={listboxId}
            className={s.list}
            role="listbox"
            aria-label={label}
            aria-busy={loading || undefined}
            /* Нажатие по строке не должно уводить фокус из поля: в Safari он ушёл бы в body */
            onMouseDown={(event) => event.preventDefault()}
          >
            {sections.map(({ group, captionId, rows: groupRows }) =>
              // Группа без строк — это подпись над пустотой, а не «спрятанные данные»
              groupRows.length === 0 ? null : (
                <div key={group.id} className={s.group} role="group" aria-labelledby={captionId}>
                  {/* role="presentation" — приём примера APG «Listbox with grouped options»:
                      подпись даёт группе имя, но сама объектом списка не является */}
                  <SectionCaption className={s.caption} labelId={captionId} role="presentation">
                    {group.label}
                  </SectionCaption>

                  {groupRows.map(renderRow)}
                </div>
              ),
            )}
          </div>

          {/* Ветки данных лежат вне listbox: внутри него законны только option и group */}

          {loading ? (
            <div className={s.loading} data-status="loading">
              {isFilled(loadingLabel) ? (
                <span className={s.loadingLine}>
                  {/* Спиннер декоративен: о запросе сообщает aria-busy списка */}
                  <Spinner size="xs" />
                  {loadingLabel}
                </span>
              ) : null}

              {SKELETON_WIDTHS.map((width) => (
                <Skeleton key={width} shape="line" size="md" width={width} />
              ))}
            </div>
          ) : null}

          {showEmpty ? (
            <div className={s.status} data-status="empty">
              {empty}
            </div>
          ) : null}

          {showError ? (
            <div className={s.status} data-status="error">
              {error}
            </div>
          ) : null}
        </div>

        {showFooter ? (
          <>
            <Divider tone="strong" spacing="xs" inset="sm" decorative />

            <button
              type="button"
              className={s.submit}
              /* Футер — не опция: он не в обходе стрелками, зато достижим Tab, мышью и Enter */
              aria-keyshortcuts="Enter"
              onMouseDown={(event) => event.preventDefault()}
              onClick={submit}
            >
              <span className={s.submitLabel}>{submitLabel}</span>
              {/* Колпачок декоративен: сочетание уже объявлено через aria-keyshortcuts,
                  а глиф «↵» словами не читается */}
              <Kbd keys={['enter']} size="xs" variant="outlined" decorative />
            </button>
          </>
        ) : null}
      </Popover>
    </>
  )
}
